import React from 'react';
import { Mail, Phone, MapPin, Github, Linkedin, Twitter } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const handlePageNavigation = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path);
      // Scroll to top after navigation
      window.scrollTo({ top: 0 });
    }
  };

  const handleNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" onClick={handleHomeClick} className="inline-flex items-center hover:opacity-90 transition-opacity">
              <Logo variant="light" />
            </Link>
            <p className="text-sm">
              Empowering businesses with comprehensive privacy compliance solutions.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="hover:text-brand-light transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" className="hover:text-brand-light transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="#" className="hover:text-brand-light transition-colors">
                <Github className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="/" onClick={handleHomeClick} className="hover:text-brand-light transition-colors">Home</a></li>
              <li>
                <button
                  onClick={() => handlePageNavigation('/learn-more')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Learn More
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('how-it-works')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('assessments')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Assessments
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => handlePageNavigation('/privacy')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => handlePageNavigation('/terms')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <button
                  onClick={() => handlePageNavigation('/security')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Security
                </button>
              </li>
              <li>
                <button
                  onClick={() => handlePageNavigation('/compliance')}
                  className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
                >
                  Compliance
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>contact@TurboPIA.com</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <span>+1 (555) PRIVACY</span>
              </li>
              <li className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>Austin, TX</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm">
              © {currentYear} PrivacyQuest Consulting LLC. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <button
                onClick={() => handlePageNavigation('/accessibility')}
                className="text-gray-300 hover:text-brand-light transition-colors bg-transparent border-none p-0 cursor-pointer font-inherit"
              >
                Accessibility
              </button>
              <Link to="/sitemap" className="hover:text-brand-light transition-colors">Sitemap</Link>
              <Link to="/cookies" className="hover:text-brand-light transition-colors">Cookie Preferences</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}